import PropTypes from 'prop-types';
import React, { useId, useState } from 'react';
import { focusField } from './validation';

// Layout pieces for forms (see FORMS.md): FormGrid lays fields out, FormSection
// groups related fields under a heading, FormActions holds the buttons.

// Responsive grid. One column on phones, `columns` from tablet up. Give a
// field span='full' (or 2 / 3) to make it wider than one cell.
export const FormGrid = ({ columns = 2, className = '', children }) => (
  <div
    className={`hy-grid hy-grid--${columns} ${className}`.trim()}
    style={{ '--hy-grid-cols': columns }}
  >
    {children}
  </div>
);

export const RequiredNote = () => (
  <p className='hy-required-note'>
    <span className='hy-field__req' aria-hidden='true'>
      *
    </span>{' '}
    Required field
  </p>
);

export const FormSection = ({
  title,
  description,
  icon,
  columns = 2,
  collapsible = false,
  defaultOpen = true,
  hasError = false,
  actions,
  className = '',
  children,
}) => {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = `hy-section-${uid}`;
  // A collapsed section must not hide a field the user has to fix.
  const expanded = open || hasError;

  return (
    <section className={`hy-section ${className}`.trim()}>
      <header className='hy-section__header'>
        <div className='hy-section__heading'>
          {icon && (
            <i className={`${icon} hy-section__icon`} aria-hidden='true' />
          )}
          <div>
            <h3 className='hy-section__title'>{title}</h3>
            {description && <p className='hy-section__desc'>{description}</p>}
          </div>
        </div>
        <div className='hy-section__tools'>
          {actions}
          {collapsible && (
            <button
              type='button'
              className='hy-section__toggle'
              aria-expanded={expanded}
              aria-controls={bodyId}
              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${title}`}
              onClick={() => setOpen(value => !value)}
            >
              <i
                className={
                  expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
                }
                aria-hidden='true'
              />
            </button>
          )}
        </div>
      </header>
      <div id={bodyId} className='hy-section__body' hidden={!expanded}>
        <FormGrid columns={columns}>{children}</FormGrid>
      </div>
    </section>
  );
};

// Footer with the form buttons. Order inside: tertiary, secondary, primary
// (see BUTTONS.md). `sticky` pins it to the bottom of long page forms.
export const FormActions = ({
  align = 'end',
  sticky = false,
  children,
  className = '',
}) => (
  <div
    className={`hy-actions hy-actions--${align}${
      sticky ? ' hy-actions--sticky' : ''
    } ${className}`.trim()}
  >
    {children}
  </div>
);

// Shown after a failed submit: how many fields need work, each one a link
// that jumps to the field. `labels` maps field name to the label to show.
export const FormErrorSummary = ({ errors = {}, labels = {} }) => {
  const entries = Object.entries(errors).filter(([, message]) => message);
  if (entries.length === 0) return null;

  return (
    <div className='hy-error-summary' role='alert'>
      <div className='hy-error-summary__title'>
        <i className='ri-error-warning-line' aria-hidden='true' />
        {entries.length === 1
          ? '1 field needs your attention'
          : `${entries.length} fields need your attention`}
      </div>
      <ul>
        {entries.map(([name, message]) => (
          <li key={name}>
            <button
              type='button'
              className='hy-error-summary__link'
              onClick={() => focusField(name)}
            >
              {labels[name] || name}
            </button>
            <span>: {message}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

FormGrid.propTypes = {
  columns: PropTypes.oneOf([1, 2, 3, 4]),
  className: PropTypes.string,
  children: PropTypes.node,
};
FormSection.propTypes = {
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  icon: PropTypes.string,
  columns: PropTypes.oneOf([1, 2, 3, 4]),
  collapsible: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  hasError: PropTypes.bool,
  actions: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node,
};
FormActions.propTypes = {
  align: PropTypes.oneOf(['start', 'end', 'between']),
  sticky: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};
FormErrorSummary.propTypes = {
  errors: PropTypes.object,
  labels: PropTypes.object,
};
